import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Phone, MapPin, Clock, Mail, Facebook, Instagram } from 'lucide-react';
import { toast } from 'sonner';

export const ContactInfo = () => {
  const handleCallClick = () => {
    window.location.href = 'tel:0298801612';
    toast.success('Lancement de l\'appel...');
  };

  const handleMapClick = () => {
    window.open('https://www.google.com/maps/search/?api=1&query=35+rue+Hervé+de+Guébriant+29800+Landerneau', '_blank');
  };

  const hours = [
    { day: 'Mardi', hours: '11h30 - 13h45 et 18h00 - 21h00' },
    { day: 'Mercredi & Jeudi', hours: '11h30 - 13h45 et 18h00 - 20h45' },
    { day: 'Vendredi & Samedi', hours: '11h30 - 13h45 et 18h00 - 21h00' },
    { day: 'Dimanche', hours: '18h00 - 21h00' },
  ];

  return (
    <section id="contact" className="py-16 sm:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-foreground mb-4 sm:mb-6">
            Contact & Horaires
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Retrouvez-nous à Landerneau pour savourer nos spécialités
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {/* Contact Information Card */}
          <Card className="border-border shadow-medium bg-card">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold text-foreground mb-6">Nous Contacter</h3>
              
              {/* Phone */}
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-grow">
                  <h4 className="font-semibold text-foreground mb-1">Téléphone</h4>
                  <a href="tel:0298801612" className="text-muted-foreground hover:text-primary transition-smooth">
                    02 98 80 16 12
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-grow">
                  <h4 className="font-semibold text-foreground mb-1">Adresse</h4>
                  <p className="text-muted-foreground">
                    35 rue Hervé de Guébriant<br />
                    29800 Landerneau
                  </p>
                </div>
              </div>

              {/* Social Media */}
              <div className="flex items-start space-x-4 mb-8">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-grow">
                  <h4 className="font-semibold text-foreground mb-2">Réseaux Sociaux</h4>
                  <div className="flex space-x-3">
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        toast.info('Retrouvez-nous sur Facebook: pizza et gourmandises');
                      }}
                      className="w-10 h-10 rounded-full bg-muted hover:bg-primary/20 flex items-center justify-center transition-smooth"
                      aria-label="Facebook"
                    >
                      <Facebook className="w-5 h-5 text-primary" />
                    </a>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        toast.info('Retrouvez-nous sur Instagram: pizza et gourmandises');
                      }}
                      className="w-10 h-10 rounded-full bg-muted hover:bg-primary/20 flex items-center justify-center transition-smooth"
                      aria-label="Instagram"
                    >
                      <Instagram className="w-5 h-5 text-primary" />
                    </a>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleCallClick}
                  className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground shadow-soft hover:shadow-medium transition-all"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Appeler maintenant
                </Button>
                <Button
                  onClick={handleMapClick}
                  variant="outline"
                  className="flex-1 border-primary/30 text-primary hover:bg-primary/10 transition-smooth"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Itinéraire
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Hours Card */}
          <Card className="border-border shadow-medium bg-card">
            <CardContent className="p-8">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-2xl font-bold text-foreground">Horaires d'Ouverture</h3>
              </div>

              <div className="space-y-4">
                {hours.map((item, index) => (
                  <div
                    key={index}
                    className="flex items-start justify-between py-4 border-b border-border last:border-0"
                  >
                    <span className="font-medium text-foreground">{item.day}</span>
                    <span className="text-muted-foreground text-right text-sm sm:text-base">{item.hours}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map Image/Placeholder */}
        <div className="mt-12 max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-lg shadow-medium h-96 bg-muted">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2683.4!2d-4.25!3d48.45!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDI3JzAwLjAiTiA0wrAxNScwMC4wIlc!5e0!3m2!1sfr!2sfr!4v1234567890123!5m2!1sfr!2sfr"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="P&G Pizza & Gourmandises Location"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactInfo;